import React, { useEffect } from 'react'
import { Link } from 'react-router-dom'
import Navigation from '@/components/Navigation'
import Footer from '@/components/Footer'
import {ShieldCheck, Download, Lock, Star, ArrowRight, CheckCircle} from 'lucide-react'
import { useProducts } from '@/hooks/useProducts'

export default function HomePage() {
  const { products, loading, fetchFeaturedProducts } = useProducts()

  useEffect(() => {
    fetchFeaturedProducts()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const testimonials = [
    {
      name: 'Michael Chen',
      company: 'HostPro Solutions',
      content: 'The WordPress integration plugin saved us countless hours. Setup was straightforward and the sync is flawless.',
      rating: 5
    },
    {
      name: 'Sarah Johnson',
      company: 'CloudHost Inc',
      content: 'Finally, a secure and licensed solution. No more risky nulled scripts. Worth every penny for peace of mind.',
      rating: 5
    },
    {
      name: 'David Martinez',
      company: 'WebServe Hosting',
      content: 'The Elementor integration transformed our client area. Professional support team and excellent documentation.',
      rating: 5
    }
  ]

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-white py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex justify-center mb-6">
            <div className="flex items-center space-x-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium">
              <ShieldCheck className="w-4 h-4" />
              <span>Official Licensed Plugins</span>
            </div>
          </div>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
            Best WHMCS WordPress Plugins & Integrations
          </h1>
          
          <p className="text-xl text-slate-600 mb-8 max-w-3xl mx-auto">
            Secure, verified plugins to bridge WHMCS with WordPress, Elementor, and cPanel.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/plugins"
              className="inline-flex items-center justify-center px-8 py-4 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-all hover:shadow-lg"
            >
              Browse All Plugins
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <a
              href="#why-choose"
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-blue-600 font-semibold rounded-lg border-2 border-blue-600 hover:bg-blue-50 transition-all"
            >
              Why Choose Us
            </a>
          </div>

          {/* Trust Indicators */}
          <div className="mt-12 flex flex-wrap justify-center gap-8 text-sm text-slate-600">
            <div className="flex items-center space-x-2">
              <ShieldCheck className="w-5 h-5 text-green-500" />
              <span>100% Secure Code</span>
            </div>
            <div className="flex items-center space-x-2">
              <Download className="w-5 h-5 text-blue-500" />
              <span>Instant Digital Downloads</span>
            </div>
            <div className="flex items-center space-x-2">
              <Lock className="w-5 h-5 text-blue-600" />
              <span>Verified Developers</span>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">Featured WHMCS Plugins</h2>
            <p className="text-lg text-slate-600">Professional integrations for your hosting business</p>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-slate-600">Loading featured products...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {products && products.length > 0 ? products.map((product) => (
                <div
                  key={product._id}
                  className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                >
                  {product.imageUrl && (
                    <img 
                      src={product.imageUrl} 
                      alt={product.name}
                      className="w-full h-48 object-cover rounded-lg mb-4"
                    />
                  )}
                  <div className="flex items-center justify-between mb-4">
                    <span className="bg-blue-100 text-blue-700 text-xs font-semibold px-3 py-1 rounded-full">
                      {product.category}
                    </span>
                    <span className="text-2xl font-bold text-slate-900">${product.price}</span>
                  </div>
                  
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{product.name}</h3>
                  <p className="text-slate-600 mb-6">{product.shortDescription}</p>

                  <Link
                    to={`/product/${product.slug}`}
                    className="block w-full text-center px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Learn More
                  </Link>

                  <div className="mt-4 flex items-center space-x-2 text-sm text-slate-500">
                    <Download className="w-4 h-4" />
                    <span>Instant Download</span>
                  </div>
                </div>
              )) : (
                <div className="col-span-3 text-center py-12 text-slate-600">
                  <p>No featured products available at this time.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Why Choose Licensed Plugins */}
      <section id="why-choose" className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Why Choose Licensed Plugins?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Security and reliability you can trust. Don't risk your business with unverified scripts.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <ShieldCheck className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">100% Secure Code</h3>
              <p className="text-slate-600">
                All plugins are thoroughly vetted and free from malicious code or backdoors.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <CheckCircle className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Regular Updates</h3>
              <p className="text-slate-600">
                Licensed plugins receive security patches and compatibility updates promptly.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <Lock className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">No Legal Risks</h3>
              <p className="text-slate-600">
                Avoid copyright infringement and maintain compliance with official licenses.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <Star className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Expert Support</h3>
              <p className="text-slate-600">
                Access documentation and technical support from verified developers.
              </p>
            </div>
          </div>

          <div className="mt-12 bg-red-50 border-l-4 border-red-400 p-6 rounded-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <ShieldCheck className="w-6 h-6 text-red-600" />
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-semibold text-red-900 mb-2">
                  Warning: Avoid Unverified Scripts
                </h3>
                <p className="text-red-800">
                  Using unlicensed or modified plugins can expose your business to security vulnerabilities, 
                  data breaches, and legal consequences. Choose official, licensed solutions for peace of mind.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Customer Testimonials */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Trusted by Hosting Providers
            </h2>
            <p className="text-lg text-slate-600">See what our customers say about our plugins</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-slate-50 p-6 rounded-xl border border-gray-200"
              >
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-slate-700 mb-4 italic">"{testimonial.content}"</p>
                <div>
                  <p className="font-semibold text-slate-900">{testimonial.name}</p>
                  <p className="text-sm text-slate-500">{testimonial.company}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-slate-600">Get answers to common questions about our plugins</p>
          </div>

          <div className="space-y-6">
            <div className="bg-slate-50 p-6 rounded-xl border border-gray-200">
              <h3 className="text-xl font-bold text-slate-900 mb-3">
                Is the code open source?
              </h3>
              <p className="text-slate-700">
                Yes, we provide unencrypted code for maximum transparency. You can review every line of code 
                to ensure security and compatibility with your WHMCS installation. This also allows you to 
                customize the plugins to meet your specific needs.
              </p>
            </div>

            <div className="bg-slate-50 p-6 rounded-xl border border-gray-200">
              <h3 className="text-xl font-bold text-slate-900 mb-3">
                Do you support Elementor?
              </h3>
              <p className="text-slate-700">
                Yes, our WordPress integrations are fully compatible with Elementor. You can use Elementor's 
                powerful drag-and-drop page builder to design stunning WHMCS client areas, customize login pages, 
                and create seamless user experiences. Our Elementor WHMCS Client Area Designer plugin provides 
                complete visual customization capabilities.
              </p>
            </div>

            <div className="bg-slate-50 p-6 rounded-xl border border-gray-200">
              <h3 className="text-xl font-bold text-slate-900 mb-3">
                What payment methods do you accept?
              </h3>
              <p className="text-slate-700">
                We accept all major credit cards, PayPal, and cryptocurrency payments. All transactions are 
                processed securely, and you'll receive instant access to your purchased plugins via digital download.
              </p>
            </div>

            <div className="bg-slate-50 p-6 rounded-xl border border-gray-200">
              <h3 className="text-xl font-bold text-slate-900 mb-3">
                Do you offer refunds?
              </h3>
              <p className="text-slate-700">
                Yes, we offer a 30-day money-back guarantee on all plugins. If you're not satisfied with your 
                purchase for any reason, contact our support team within 30 days for a full refund.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-blue-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to Enhance Your WHMCS Installation?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Join hundreds of hosting providers using our secure, licensed plugins.
          </p>
          <Link
            to="/plugins"
            className="inline-flex items-center px-8 py-4 bg-white text-blue-600 font-semibold rounded-lg hover:bg-blue-50 transition-all hover:shadow-xl"
          >
            Browse All Plugins
            <ArrowRight className="ml-2 w-5 h-5" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  )
}
